import { BARN } from '../models/constants';

export interface ZonedParts {
    year:number,
    month:number, // 1 - 12
    day:number,   // 1 - 31
    hour:number,
    minute:number,
    dayOfWeek:number // 0 = Sunday
}

const WEEKDAY_INDEX:{ [key:string]:number } = {
    Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6
};

const PARTS_FORMATTER = new Intl.DateTimeFormat('en-US', {
    timeZone: BARN.TIME_ZONE,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    weekday: 'short'
});

/*
  Bookings are instants but barn hours are wall clock hours at the barn, so the calendar
  always reasons in BARN.TIME_ZONE rather than whatever timezone the phone is in.
*/
export function getZonedParts(instant:Date):ZonedParts {
    const lookup:{ [key:string]:string } = {};
    for(const part of PARTS_FORMATTER.formatToParts(instant)) {
        lookup[part.type] = part.value;
    }

    return {
        year: Number(lookup['year']),
        month: Number(lookup['month']),
        day: Number(lookup['day']),
        hour: Number(lookup['hour']),
        minute: Number(lookup['minute']),
        dayOfWeek: WEEKDAY_INDEX[lookup['weekday']]
    };
}

/* Turns a barn wall clock date plus minutes from midnight into the matching instant. */
export function toInstant(year:number, month:number, day:number, minutesFromMidnight:number):Date {
    const target = Date.UTC(year, month - 1, day, 0, minutesFromMidnight);

    let timestamp = target;
    for(let i = 0; i < 3; i++) {
        const parts = getZonedParts(new Date(timestamp));
        const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute);
        const drift = target - asUtc;
        if(drift === 0) break;
        timestamp += drift;
    }

    return new Date(timestamp);
}

export function getMinutesFromMidnight(instant:Date):number {
    const parts = getZonedParts(instant);
    return (parts.hour * 60) + parts.minute;
}

/* Sunday of the barn week that contains the given instant, as a { year, month, day }. */
export function getWeekStartDate(instant:Date):{ year:number, month:number, day:number } {
    const parts = getZonedParts(instant);
    const anchor = Date.UTC(parts.year, parts.month - 1, parts.day, 12);
    const sunday = new Date(anchor - (parts.dayOfWeek * 24 * 60 * 60 * 1000));

    return { year: sunday.getUTCFullYear(), month: sunday.getUTCMonth() + 1, day: sunday.getUTCDate() };
}

export function addDays(date:{ year:number, month:number, day:number }, days:number):{ year:number, month:number, day:number } {
    const shifted = new Date(Date.UTC(date.year, date.month - 1, date.day, 12) + (days * 24 * 60 * 60 * 1000));
    return { year: shifted.getUTCFullYear(), month: shifted.getUTCMonth() + 1, day: shifted.getUTCDate() };
}

export function getDayOfWeek(date:{ year:number, month:number, day:number }):number {
    return new Date(Date.UTC(date.year, date.month - 1, date.day, 12)).getUTCDay();
}

export function toDateLiteral(date:{ year:number, month:number, day:number }):string {
    return `${date.year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`;
}

export function parseDateLiteral(value:string):{ year:number, month:number, day:number } {
    const [year, month, day] = value.split('-').map(Number);
    return { year, month, day };
}

export function formatTimeOfDay(minutesFromMidnight:number):string {
    const hour24 = Math.floor(minutesFromMidnight / 60);
    const minute = minutesFromMidnight % 60;
    const suffix = hour24 >= 12 ? 'PM' : 'AM';
    const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;

    return minute === 0
        ? `${hour12} ${suffix}`
        : `${hour12}:${String(minute).padStart(2, '0')} ${suffix}`;
}

export function toTimeLiteral(minutesFromMidnight:number):string {
    const hour = Math.floor(minutesFromMidnight / 60);
    const minute = minutesFromMidnight % 60;
    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

export function buildSlotMinutes(startMinute:number, endMinute:number):Array<number> {
    const slots:Array<number> = [];
    for(let minute = startMinute; minute < endMinute; minute += BARN.SLOT_MINUTES) {
        slots.push(minute);
    }
    return slots;
}
